import React, { useState, useEffect, useRef } from 'react';

const CounterAnimation = ({ targetNumber }) => {
  // State to track the current number for animation
  const [currentNumber, setCurrentNumber] = useState(0);

  // Reference to the container element
  const containerRef = useRef(null);

  // Duration of the animation in milliseconds
  const animationDuration = 1750; // 1.75 seconds

  // Reference to store the start time of the animation
  const startTime = useRef(0);

  // Reference to the animation frame request
  const animationRef = useRef(null);

  // Reference to the IntersectionObserver instance
  const observerRef = useRef(null);

  // Flag to track if animation has been triggered
  const animationTriggered = useRef(false);

  // Easing function for smoother animation
  const easeInOutSlowEnd = (t) => {
    return t < 0.5
      ? 8 * t * t * t * t
      : 1 - Math.pow(-2 * t + 2, 4) / 2;
  };

  // Function to update the number during animation
  const updateNumber = (timestamp) => {
    const elapsedTime = timestamp - startTime.current;
    const progress = Math.min(1, elapsedTime / animationDuration);

    const easedProgress = easeInOutSlowEnd(progress);
    const easedNumber = easedProgress * targetNumber;

    setCurrentNumber(easedNumber);

    if (elapsedTime < animationDuration) {
      animationRef.current = requestAnimationFrame(updateNumber);
    }
  };

  // Function to start the animation
  const startAnimation = () => {
    if (!animationTriggered.current) {
      animationTriggered.current = true;
      startTime.current = performance.now();
      animationRef.current = requestAnimationFrame(updateNumber);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    }
  };

  useEffect(() => {
    // Set up IntersectionObserver to trigger animation
    if (containerRef.current) {
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          startAnimation();
        }
      });

      observerRef.current.observe(containerRef.current);
    }

    // Clean up animation and observer on unmount or targetNumber change
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [targetNumber]);

  // Function to format numbers with commas
  const formatNumberWithCommas = (number) => {
    return targetNumber % 1 !== 0
      ? number.toLocaleString(undefined, { maximumFractionDigits: 2 })
      : Math.round(number).toLocaleString();
  };

  // Render the component
  return (
    <div ref={containerRef} className="counterAnimation">
      {formatNumberWithCommas(currentNumber)}
    </div>
  );
};

export default CounterAnimation;
